/**
 * AddressForm Component
 */

import { requestFetchStatesForCountry } from '../../../../redux/actions/countries';
import { FieldPostalCode } from '../../../../redux/form/utils';
import { environment } from '../../../../relay';
import { checkoutAttributesFragment, CheckoutAttributesQuery } from '../../../../relay/checkout';
import { emptyPromiseActionPayload } from '../../../../utils/promise';
import { validatePostalCode, validateString } from '../../../../utils/string';

import { find, get, map, sortBy, trimEnd } from 'lodash';
import * as React from 'react';
import * as BEM from 'react-bem-helper';
import { connect } from 'react-redux';
import { createFragmentContainer, QueryRenderer } from 'react-relay';
import { change, Field, FormSection, getFormValues } from 'redux-form';

import FormCell from '../../../molecules/forms/FormCell';
import FormRow from '../../../molecules/forms/FormRow';
import Select from '../../../molecules/forms/Select';
import TextField from '../../../molecules/forms/TextFieldWithLabel';

export const getAddressFormValidations = (prefix: string = ''): IFormValidations => ({
  [`${prefix && `${trimEnd(prefix, '.')}.`}address.company.name`]: {
    validator: validateString,
    error: 'Is it a valid company name?',
  },
  [`${prefix && `${trimEnd(prefix, '.')}.`}address.company.type`]: {
    error: 'Please select a company type.',
  },
  [`${prefix && `${trimEnd(prefix, '.')}.`}address.street`]: {
    validator: validateString,
    error: 'Is it a valid street address?',
  },
  [`${prefix && `${trimEnd(prefix, '.')}.`}address.city`]: {
    validator: validateString,
    error: 'Is it a valid city name?',
  },
  [`${prefix && `${trimEnd(prefix, '.')}.`}address.country`]: {
    error: 'Please select a country.',
  },
  [`${prefix && `${trimEnd(prefix, '.')}.`}address.state`]: {
    error: 'Please select a state or province.',
  },
  [`${prefix && `${trimEnd(prefix, '.')}.`}address.postalCode`]: {
    validator: validatePostalCode,
    validatorRequiresArguments: true,
    error: 'Code format is not valid.',
  },
});

interface IFormFields {
  address: IAddress;
}

interface IOwnStateProps {
  countryId: string;
  countryList: ICountryListState;
}

interface IOwnDispatchProps {
  setCountry: (country?: ICountry, postalCodeValidationRegExp?: string) => void;
}

interface IOwnProps {
  form: string;
  formSectionName?: string;
  viewer: GQL.IViewer;
}

type RequiredProps = Pick<IOwnProps, 'form' | 'formSectionName'>;
type AddressFormProps = IOwnProps & IOwnStateProps & IOwnDispatchProps;

const countriesForSelect = (countryOrState: ICountry[] | ICountryState[]) =>
  map<ICountry | ICountryState, ISelectStoreItem>(countryOrState, ({ code, name }) => ({
    value: code,
    label: name,
  }));

const classes = new BEM('address-form');

class AddressForm extends React.Component<AddressFormProps, {}> {
  static defaultProps: Partial<AddressFormProps> = {
    formSectionName: 'address',
  };

  companyTypesForSelect: Array<{ value: string; label: string; }>;

  constructor(props: AddressFormProps) {
    super(props);

    this.companyTypesForSelect = props.viewer.company_types!.edges!.map(({ node }) => {
      return {
        value: node!.id,
        label: node!.name!,
      };
    });

    this.companyTypesForSelect = sortBy(this.companyTypesForSelect, 'label');
  }

  onSetCountry = (item: ISelectStoreItem | null) => {
    if (item) {
      const country = find<ICountry>(this.props.countryList.countries, { code: String(item.value) });
      if (country && country.filename) {
        return this.props.setCountry(country);
      }
    }

    this.props.setCountry();
  }

  render() {
    const {
      countryId,
      countryList,
      formSectionName,
      form,
    } = this.props;

    return (
      <FormSection name={formSectionName!} {...classes() }>
        <FormRow>
          <FormCell minimumWidth={8}>
            <Field
              name="company.name"
              component={TextField}
              label="Company Name"
              colorScheme="light"
            />
          </FormCell>
          <FormCell spacer minimumWidth={1} />
          <FormCell minimumWidth={8}>
            <Select
              form={form}
              label="Company Type"
              name="company.type"
              options={this.companyTypesForSelect}
              selectName="company-type-select"
              selectProps={{
                autoBlur: true,
                clearable: false,
              }}
            />
          </FormCell>
        </FormRow>
        <FormRow>
          <FormCell minimumWidth={11}>
            <Field
              component="input"
              name="postalCodeValidationRegExp"
              type="hidden"
            />
            <Field
              name="street"
              component={TextField}
              label="Street Address"
              colorScheme="light"
            />
          </FormCell>
          <FormCell spacer minimumWidth={1} />
          <FormCell minimumWidth={5}>
            <Field
              name="unit"
              component={TextField}
              label="Suite, unit"
              note="Opt."
              colorScheme="light"
              optional
            />
          </FormCell>
        </FormRow>
        <FormRow>
          <FormCell minimumWidth={11}>
            <Field
              name="city"
              component={TextField}
              label="City"
              colorScheme="light"
            />
          </FormCell>
          <FormCell spacer minimumWidth={1} />
          <FormCell minimumWidth={5}>
            <FieldPostalCode
              name="postalCode"
              component={TextField}
              label="Postal/Zip Code"
              colorScheme="light"
            />
          </FormCell>
        </FormRow>
        <FormRow>
          <FormCell minimumWidth={8}>
            <Select
              form={form}
              label="Country"
              name="country"
              onUpdate={this.onSetCountry}
              options={countriesForSelect(countryList.countries)}
              selectName="country-select"
              selectProps={{
                autoBlur: true,
                clearable: false,
              }}
            />
          </FormCell>
          <FormCell spacer minimumWidth={1} />
          <FormCell minimumWidth={8}>
            <Select
              disabled={!!!countryId}
              form={form}
              label="State or province"
              name="state"
              options={countriesForSelect(countryList.states[countryId])}
              selectName="state-select"
              selectProps={{
                autoBlur: true,
                clearable: false,
              }}
            />
          </FormCell>
        </FormRow>
      </FormSection>
    );
  }
}

const ConnectedAddressForm = connect<IOwnStateProps, IOwnDispatchProps, IOwnProps, AddressFormProps>(
  (state: IStoreState, { form, formSectionName = 'address' }) => {
    const formState = getFormValues<IFormFields, IStoreState>(form)(state);

    return {
      countryId: get(formState, `${formSectionName}.country`, ''),
      countryList: state.countryList,
    };
  },
  (dispatch, { form, formSectionName = 'address' }) => ({
    setCountry: (country, postalCodeValidationRegExp) => {
      const input = 'postalCodeValidationRegExp';
      dispatch(change(form, input, postalCodeValidationRegExp));

      if (country) {
        dispatch(requestFetchStatesForCountry({
          country,
          ...emptyPromiseActionPayload,
        }));
      }
    },
  }),
  (stateProps, dispatchProps, ownProps) => ({
    ...ownProps,
    ...stateProps,
    setCountry: (country) => {
      let postalCodeValidationRegExp = '';
      if (country && stateProps.countryList.postalCodes.hasOwnProperty(country.code)) {
        postalCodeValidationRegExp = stateProps.countryList.postalCodes[country.code];
      }

      dispatchProps.setCountry(country, postalCodeValidationRegExp);
    },
  }),
)(AddressForm);

const RelayAddressForm = createFragmentContainer(
  ConnectedAddressForm,
  checkoutAttributesFragment,
);

export default function AddressFormQueryRenderer(componentProps: RequiredProps) {
  return (
    <QueryRenderer
      environment={environment}
      query={CheckoutAttributesQuery}
      variables={{}}
      render={({ error, props }) => {
        if (error) {
          return <div>{error.message}</div>;
        } else if (props) {
          return <RelayAddressForm {...props} {...componentProps} />;
        }
        return null;
      }}
    />
  );
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/public/components/organisms/forms/AddressForm/index.tsx